'use client';

import { TeeTimeNoResults } from '@/components/tables/tee-time/tee-time-no-results';
import { TimezoneSelector } from '@/components/timezone/timezone-selector';
import { TeeTime } from '@spikemark/rest-api';
import { useFetch } from '@spikemark/shared-hooks';
import { AvatarWithName } from '@spikemark/ui/components/avatar-with-name';
import { ClientTime } from '@spikemark/ui/components/client-time';
import { LoadingSpinner } from '@spikemark/ui/components/loading-spinner';
import { PlayerLink } from '@spikemark/ui/components/navigation/entity-links';
import { FilterSelector } from '@spikemark/ui/components/query/filter-selector';
import { Toolbar } from '@spikemark/ui/components/toolbar';
import { getDateFromServer } from '@spikemark/ui/utils/date';
import { displayTeamName } from '@spikemark/ui/utils/format';
import orderBy from 'lodash/orderBy';
import { useSearchParams } from 'next/navigation';
import { FC, Suspense, useMemo } from 'react';
export const TeeTimeGrid: FC<{
  tournamentId: string;
  rounds: number;
  plannedRounds: number;
}> = ({
  tournamentId,
  rounds,
  plannedRounds
}) => {
  const roundsArray = [...Array(plannedRounds)];
  const searchParams = useSearchParams();
  const roundId = String(parseInt(searchParams?.get('round') ?? '0', 10) || Math.min(plannedRounds ?? 0, (rounds ?? 0) + 1));
  const {
    data: results,
    isFetching
  } = useFetch<TeeTime[]>(`/api/tournaments/${tournamentId}/teetimes/${roundId}`);
  const sortedResults = useMemo(() => {
    return orderBy(results, [x => getDateFromServer(x.teeTime), x => x.startingHole]);
  }, [results]);
  return <div data-sentry-component="TeeTimeGrid" data-sentry-source-file="tee-time-grid.tsx">
      <Toolbar variant="ghost" suppressMobileMenu className="flex p-4 flex-col lg:flex-row lg:items-center lg:justify-between" data-sentry-element="Toolbar" data-sentry-source-file="tee-time-grid.tsx">
        <FilterSelector value={roundId} queryParamKey="round" options={roundsArray?.map((_, i) => String(i + 1))} className="lg:w-[180px]" prefix="Round " data-sentry-element="FilterSelector" data-sentry-source-file="tee-time-grid.tsx" />
        <TimezoneSelector data-sentry-element="TimezoneSelector" data-sentry-source-file="tee-time-grid.tsx" />
      </Toolbar>
      <div className="px-2 lg:px-4">
        <div className="bg-white rounded-lg shadow-xs overflow-clip">
          {isFetching ? <LoadingSpinner /> : sortedResults?.length ? <table className="table-auto w-full">
              <thead className="thead-secondary sticky top-main-with-tab-nav-mobile lg:top-main-with-tab-nav z-10">
                <tr className="uppercase">
                  <th className="px-4 lg:px-6 py-2">Time</th>
                  <th className="px-2 lg:px-4 py-2">Hole</th>
                  <th className="px-2 lg:px-4 py-2 text-left">Players</th>
                </tr>
              </thead>
              <tbody>
                {sortedResults.map((result, index) => <tr key={index} className="even:bg-gray-50 align-top">
                    <td className="px-4 lg:px-6 py-3">
                      <ClientTime time={result.teeTime} />
                    </td>
                    <td className="px-2 lg:px-4 py-3">
                      {result.startingHole}
                      {result.groupLabel ?? ''}
                    </td>
                    <td className="px-2 lg:px-4 py-2">
                      <div className="grid grid-cols-1 lg:grid-cols-4 gap-2">
                        {result.playersInfo?.map(player => <AvatarWithName className="text-sm" key={player.playerId} variant="small" playerName={player.playerName} schoolName={player.schoolName} schoolLogoThumbnail={player.schoolLogo} userPicture={player.playerLogo}>
                            <PlayerLink playerId={player.playerId} suppressChevron>
                              <h3 className="font-bold">{player.playerName}</h3>
                            </PlayerLink>
                            <p>
                              {displayTeamName(player.schoolName, player.teamLabel, player.playerId)}
                            </p>
                          </AvatarWithName>)}
                      </div>
                    </td>
                  </tr>)}
              </tbody>
            </table> : <TeeTimeNoResults roundId={roundId} />}
        </div>
      </div>
    </div>;
};